/**
 * Notifications full-page view — paginated list with filters.
 * Reachable from navbar bell "View all" button or directly via /notifications.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BookOpen, Megaphone, GraduationCap, Briefcase, AlertCircle, X, CheckCheck, ArrowLeft } from 'lucide-react';
import apiService from '../services/api';

const TYPE_META = {
  lab_update:   { icon: BookOpen,    color: 'text-blue-600',    bg: 'bg-blue-50',    label: 'Lab Update' },
  announcement: { icon: Megaphone,   color: 'text-amber-600',   bg: 'bg-amber-50',   label: 'Announcement' },
  batch_news:   { icon: GraduationCap, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Batch News' },
  job_alert:    { icon: Briefcase,   color: 'text-purple-600',  bg: 'bg-purple-50',  label: 'Job Alert' },
  system:       { icon: AlertCircle, color: 'text-slate-600',   bg: 'bg-slate-50',   label: 'System' },
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'lab_update', label: 'Labs' },
  { id: 'announcement', label: 'Announcements' },
  { id: 'batch_news', label: 'Batches' },
  { id: 'job_alert', label: 'Jobs' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ notifications: [], total: 0, unread_count: 0, pages: 1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const r = await apiService.getNotifications({ page: p, limit: 20, filter: f });
      setData(r);
    } catch {
      setData({ notifications: [], total: 0, unread_count: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, filter); }, [page, filter, load]);

  const changeFilter = (f) => { setFilter(f); setPage(1); };

  const onMarkAll = async () => {
    try {
      await apiService.markAllNotificationsRead();
      load(page, filter);
    } catch { /* ignore */ }
  };

  const onItemClick = async (n) => {
    try {
      if (!n.is_read) await apiService.markNotificationRead(n.id);
    } catch { /* ignore */ }
    if (n.link) navigate(n.link);
    else load(page, filter);
  };

  const onDismiss = async (e, id) => {
    e.stopPropagation();
    try {
      await apiService.dismissNotification(id);
      load(page, filter);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notifications-page">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
              <Bell className="w-6 h-6" /> Notifications
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {data.unread_count > 0
                ? `${data.unread_count} unread of ${data.total} total`
                : `${data.total} notifications`}
            </p>
          </div>
          {data.unread_count > 0 && (
            <button
              onClick={onMarkAll}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition"
              data-testid="notifications-mark-all-read"
            >
              <CheckCheck className="w-4 h-4" />
              Mark all read
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => changeFilter(f.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition ${
                filter === f.id
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`notifications-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="space-y-2">
          {loading && (
            <div className="bg-white rounded-xl p-8 text-center text-slate-500">Loading…</div>
          )}
          {!loading && data.notifications.length === 0 && (
            <div className="bg-white rounded-xl p-12 text-center" data-testid="notifications-empty">
              <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <div className="text-base font-semibold text-slate-700">No notifications</div>
              <div className="text-sm text-slate-500 mt-1">
                {filter === 'unread'
                  ? "You've read everything. Nice work."
                  : "When something new happens (lab updates, batch news, jobs), you'll see it here."}
              </div>
            </div>
          )}
          {!loading && data.notifications.map((n) => {
            const meta = TYPE_META[n.type] || TYPE_META.system;
            const Icon = meta.icon;
            return (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                className={`w-full text-left bg-white rounded-xl p-4 flex items-start gap-3 hover:shadow-md transition border ${
                  n.is_read ? 'border-slate-100' : 'border-blue-200 bg-blue-50/30'
                }`}
                data-testid={`notification-row-${n.id}`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg ${meta.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-wider font-bold ${meta.color} ${meta.bg} px-2 py-0.5 rounded leading-relaxed inline-flex items-center`}>
                        {meta.label}
                      </span>
                      {n.severity === 'warning' && (
                        <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded leading-relaxed inline-flex items-center">
                          Important
                        </span>
                      )}
                      {n.severity === 'critical' && (
                        <span className="text-[10px] uppercase font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded leading-relaxed inline-flex items-center">
                          Critical
                        </span>
                      )}
                      {n.requires_action && (
                        <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded leading-relaxed inline-flex items-center">
                          Action needed
                        </span>
                      )}
                    </div>
                    {!n.is_read && (
                      <span className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-blue-500 mt-1" />
                    )}
                  </div>
                  <div className="text-base font-semibold text-slate-800 mt-1">{n.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{n.message}</div>
                  <div className="text-xs text-slate-400 mt-2">{timeAgo(n.created_at)}</div>
                </div>
                <button
                  onClick={(e) => onDismiss(e, n.id)}
                  className="flex-shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  aria-label="Dismiss"
                  data-testid={`notification-row-dismiss-${n.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </button>
            );
          })}
        </div>

        {/* Pagination */}
        {!loading && data.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm font-medium hover:bg-slate-100 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-500">Page {page} of {data.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
              disabled={page >= data.pages}
              className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-sm font-medium hover:bg-slate-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
